import {
  LOCATION_PRECISION_LABELS,
  PRICE_MODE_LABELS,
  formatDateTime,
} from "../lib/labels";
import { EXPIRY_PRESETS } from "../lib/expiry";
import { WizardProgress } from "../lib/wizard";

function propertyImagesById(property) {
  const map = new Map();
  (Array.isArray(property && property.images) ? property.images : []).forEach((image) => {
    if (image && image.id != null) map.set(image.id, image);
  });
  return map;
}

function effectiveReviewExpiresAt(draft, payload) {
  return (payload && payload.expires_at) ?? (draft && draft.expires_at_original) ?? "";
}

function expiryLabel(draft, payload) {
  const iso = effectiveReviewExpiresAt(draft, payload);
  const preset = EXPIRY_PRESETS.find((option) => option.value === draft.expiry_preset);
  const when = formatDateTime(iso);
  if (draft.expiry_preset === "custom" || (draft.expiry_dirty === false && draft.expires_at_original)) {
    return when ? `Інша дата · до ${when}` : "Інша дата";
  }
  return when ? `${preset ? preset.label : "30 днів"} · до ${when}` : (preset && preset.label) || "";
}

function PresentationReviewSummary({ draft, payload, property }) {
  const images = propertyImagesById(property);
  const mediaIds = Array.isArray(draft.mediaIds) ? draft.mediaIds : [];
  const priceModeLabel = PRICE_MODE_LABELS[draft.price_mode] || draft.price_mode;
  const locationLabel =
    LOCATION_PRECISION_LABELS[draft.location_precision] || draft.location_precision;
  const note = (draft.presentation_note || "").trim();
  const avatarState = draft.include_contact_avatar
    ? "Фото профілю буде показано"
    : "Без фото профілю";

  return (
    <section className="presentation-review" data-testid="presentation-review">
      <WizardProgress currentIndex={4} />
      <h3>Перевірка перед збереженням</h3>
      <p className="presentation-review__notice" data-testid="presentation-review-notice">
        Точна презентація для клієнта формується сервером після збереження.
      </p>

      <section className="presentation-review__card" data-testid="presentation-review-private">
        <h4>Лише для вас</h4>
        <p className="presentation-editor__help">Клієнт не побачить CRM-прив'язку і внутрішню назву.</p>
        <dl>
          <div>
            <dt>Клієнт CRM</dt>
            <dd>{draft.client && draft.client.name ? draft.client.name : "Без прив'язки до клієнта"}</dd>
          </div>
          <div>
            <dt>Внутрішня назва</dt>
            <dd>{draft.internal_label || "Без внутрішньої назви"}</dd>
          </div>
        </dl>
      </section>

      <section className="presentation-review__card" data-testid="presentation-review-public">
        <h4>Побачить клієнт</h4>
        <dl>
          <div>
            <dt>Ціна</dt>
            <dd>
              {draft.price_mode === "custom_price"
                ? `${priceModeLabel}: ${draft.custom_price} ${draft.custom_currency}`
                : priceModeLabel}
            </dd>
          </div>
          <div>
            <dt>Точність адреси</dt>
            <dd>{locationLabel}</dd>
          </div>
          <div>
            <dt>Нотатка для клієнта</dt>
            <dd>{note || "Без нотатки"}</dd>
          </div>
          <div>
            <dt>Медіа</dt>
            <dd>
              {mediaIds.length === 0 ? (
                "Без медіа"
              ) : (
                <ol className="presentation-review__media">
                  {mediaIds.map((imageId, index) => {
                    const image = images.get(imageId);
                    const kind = image && image.kind === "plan" ? "План" : "Фото";
                    return (
                      <li key={`${imageId}-${index}`}>
                        {image && image.image ? (
                          <img src={image.image} alt="" />
                        ) : (
                          <span className="presentation-review__media-fallback" />
                        )}
                        <span className="presentation-media-picker__kind">{kind}</span>
                        {index === 0 ? (
                          <span className="presentation-media-picker__hero">Головне</span>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              )}
            </dd>
          </div>
          <div>
            <dt>Відео</dt>
            <dd>{draft.include_video ? "Показати, якщо є на об'єкті" : "Не показувати"}</dd>
          </div>
          <div>
            <dt>Контакт</dt>
            <dd>
              {[
                draft.contact_name || "Без імені",
                draft.contact_phone || "без телефону",
                draft.contact_email || "без email",
              ].join(" · ")}
            </dd>
          </div>
          <div>
            <dt>Фото профілю</dt>
            <dd data-testid="presentation-review-avatar">{avatarState}</dd>
          </div>
          <div>
            <dt>Термін дії</dt>
            <dd
              data-testid="presentation-review-expiry"
              data-expires-at={effectiveReviewExpiresAt(draft, payload)}
            >
              {expiryLabel(draft, payload)}
            </dd>
          </div>
        </dl>
      </section>
    </section>
  );
}

export default PresentationReviewSummary;
